import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem,
  Avatar, Button, Divider, ListItemIcon
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getCurrentLanguage } from '../../utils/navigation';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLanguageMenu = (event) => setLanguageAnchorEl(event.currentTarget);
  const handleLanguageClose = () => setLanguageAnchorEl(null);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    handleLanguageClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/${getCurrentLanguage()}/auth/login`);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getNavLinks = () => {
    const lang = getCurrentLanguage();
    if (user?.role === 'student') {
      return [
        { label: t('nav.dashboard'), path: `/${lang}/student/dashboard` },
        { label: t('marketplace.searchTeachers', 'Find Teachers'), path: `/${lang}/student/search` },
        { label: 'My Lessons', path: `/${lang}/student/my-lessons` },
        { label: 'Messages', path: `/${lang}/student/messages` },
      ];
    }
    if (user?.role === 'admin') {
      return [
        { label: t('nav.dashboard'), path: `/${lang}/admin/dashboard` },
        { label: 'Teacher Approval', path: `/${lang}/admin/teacher-approval` },
      ];
    }
    return [];
  };

  const isActive = (path) => {
    const segment = path.split('/').pop();
    return window.location.pathname.includes(`/${segment}`);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #f0f0f0',
        borderRadius: 0
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo/Brand */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => navigate(`/${getCurrentLanguage()}`)}
        >
          <Box
            component="img"
            src="/images/lingoply-logo.jpg"
            alt="Lingoply"
            sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700, color: '#F5A623', fontSize: '1.2rem',
              letterSpacing: '-0.02em',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Lingooply
          </Typography>
        </Box>

        {/* Nav Links */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, flex: 1, ml: 3 }}>
          {getNavLinks().map((link, i) => (
            <Button
              key={i}
              onClick={() => navigate(link.path)}
              sx={{
                color: isActive(link.path) ? '#F5A623' : '#555',
                textTransform: 'none',
                fontWeight: isActive(link.path) ? 700 : 500,
                fontSize: '0.9rem',
                borderBottom: isActive(link.path) ? '2px solid #F5A623' : '2px solid transparent',
                borderRadius: 0, px: 2, py: 1,
                '&:hover': { color: '#F5A623', backgroundColor: 'rgba(245,166,35,0.04)' }
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Language Switcher */}
          <IconButton onClick={handleLanguageMenu} size="small" sx={{ color: '#888', '&:hover': { color: '#F5A623' } }}>
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={handleLanguageClose}
            PaperProps={{
              elevation: 2,
              sx: {
                minWidth: 120, mt: 1.5, borderRadius: '10px',
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem',
                  '&:hover': { backgroundColor: '#FFF8F0', color: '#F5A623' }
                }
              }
            }}
          >
            <MenuItem onClick={() => handleLanguageChange('ar')} selected={i18n.language === 'ar'}>
              العربية
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('en')} selected={i18n.language === 'en'}>
              English
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('fr')} selected={i18n.language === 'fr'}>
              Français
            </MenuItem>
          </Menu>

          {/* Profile Menu */}
          <IconButton onClick={handleMenu} size="small" sx={{ ml: 0.5 }}>
            <Avatar
              src={user?.photoURL}
              sx={{ width: 36, height: 36, bgcolor: '#F5A623', color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}
            >
              {user?.displayName?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 2,
              sx: {
                minWidth: 220, mt: 1.5, borderRadius: '10px',
                '& .MuiMenuItem-root': {
                  px: 2, py: 1.2, gap: 1.5, fontSize: '0.875rem',
                  '&:hover': { backgroundColor: '#FFF8F0' }
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>{user?.displayName || 'User'}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate(`/${getCurrentLanguage()}/${user?.role || 'student'}/profile`)}>
              <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
              {t('nav.profile')}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{ color: '#f44336 !important', '&:hover': { backgroundColor: 'rgba(244,67,54,0.04) !important' } }}
            >
              <ListItemIcon sx={{ color: '#f44336' }}><LogoutIcon fontSize="small" /></ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
